import {
  countByStatus,
  getAllIds,
  useGetAllBibliographies,
} from "@/api/bibliography/bibliography";
import NewBibliographyDialog from "@/components/bibliography/NewBibliographyDialog";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { BibliographyLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { DataTable } from "@/components/ui/datatable";
import {
  bibliographyAdvancedSearchConfig,
  bibliographyColumns,
} from "@/config/dashboard/bibliography";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Bibliography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "bibliography-table-preferences";
const flatVisibleBibliographyColumns = [
  ...bibliographyColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const BibliographyDashboard = () => {
  const { t } = useTranslation();

  const {
    countByStatus: count,
    currentCount: currentCount,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Bibliography>({
    resourceName: "bibliography",
    resourceCounter: countByStatus,
    statusField: "bibliographiePublicationStatus",
  });

  const { data, isFetching } = useGetAllBibliographies(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Bibliography>({
    documentType: "bibliography",
    publicationStatusField: "bibliographiePublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/bibliography/${row.id}`, row.id),
  });

  const columns: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [...generateTable(bibliographyColumns, "bibliography"), actionsColumn];
  }, [actionsColumn]);


  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisibleBibliographyColumns);
    }
    return flatVisibleBibliographyColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const defaultVisibility = bibliographyColumns.reduce(
        (acc, column) => ({
          ...acc,
          [column.key]: column.visible || false,
        }),
        {} as Record<string, boolean>,
      );
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        if (visibility && typeof visibility === "object") {
          const knownKeys = new Set(bibliographyColumns.map((column) => column.key));
          const savedKeys = Object.keys(visibility as Record<string, boolean>);
          const hasUnknownKey = savedKeys.some((key) => !knownKeys.has(key));

          if (hasUnknownKey) {
            return defaultVisibility;
          }

          return {
            ...defaultVisibility,
            ...(visibility as Record<string, boolean>),
          };
        }
      }
      return defaultVisibility;
    },
  );

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Bibliography>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleBibliographies")}
        </p>
        <GlobalSearch resource="bibliography"
          config={bibliographyAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={count}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={bibliographyAdvancedSearchConfig}
            resource="bibliography"
          />
          <AdvancedSort config={bibliographyAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createBbibliography")}
            Dialog={NewBibliographyDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(`/edit/bibliography/${row.original.id}`, row.original.id)
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="bibliography"
                      item={item}
                      publicationStatusField="bibliographiePublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/bibliography/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <BibliographyLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/bibliography/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="bibliography"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        />
      </div>
    </div>
  );
};

export default BibliographyDashboard;
